/* eslint-disable no-nested-ternary */
/* eslint-disable no-unused-vars */
/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Box } from '@chakra-ui/react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormValidationToast } from '../../../services/actionToast';
import { InputForm } from '../../inputs/forms/InputForm';
import { getFetchData, isRequestFailure, useFetch } from '../../../hooks/useFetch';
import { SelectInputForm } from '../../inputs/forms/SelectInputForm';
import { SwitchInputForm } from '../../inputs/forms/SwitchInputForm';
import { FormActions, FormRow, FormSection, FormShell } from '../../ui';
import { getFormattedLabel } from '../../../utils/FormatFunctions';

interface TeamFormProps {
  defaultValues?: any;
  view?: boolean;
}

interface FormInputsProps {
  name: string;
  value: any;
  placeholder?: string;
  label: string;
  yup: any;
}

export default function TeamForm({ defaultValues = {}, view = false }: TeamFormProps) {
  const validationToast = useFormValidationToast();
  const { itemId, institutionId } = useParams();

  const baseUrl = '/v1/admin/teams';
  const { requestApi: reqApiTeams } = useFetch(baseUrl);
  const {
    requestApi: reqApiTeachers,
    data: dataTeachers,
    loading: loadingTeachers,
  } = useFetch(
    institutionId
      ? `/v1/admin/users?group_id=4&institution_id=${institutionId}`
      : '/v1/admin/users?group_id=4'
  );
  const { data: optionsTeachers, pagination: paginationTeachers } = getFetchData(dataTeachers);
  const {
    requestApi: reqApiModules,
    data: dataModules,
    loading: loadingModules,
  } = useFetch('/v1/admin/classes');
  const { data: optionsModules, pagination: paginationModules } = getFetchData(dataModules);
  const navigate = useNavigate();
  const yupschema: any = {};
  const defaultValuesSchema: any = {};

  const formInputs: FormInputsProps[] = [
    {
      name: 'name',
      value: defaultValues.name ?? '',
      placeholder: 'Nome da turma',
      label: 'Nome',
      yup: Yup.string().required('Este campo é obrigatório!'),
    },
    {
      name: 'teacher_ids',
      value: defaultValues?.teachers?.length
        ? defaultValues.teachers.map((teacher: any) => getFormattedLabel(teacher))
        : defaultValues?.teacher
        ? [getFormattedLabel(defaultValues.teacher)]
        : [],
      placeholder: 'Selecione os professores',
      label: 'Professores',
      yup: Yup.array()
        .of(Yup.mixed())
        .min(1, 'Selecione pelo menos um professor')
        .required('Este campo é obrigatório!'),
    },
    {
      name: 'school_class_id',
      value: defaultValues?.school_class ? getFormattedLabel(defaultValues?.school_class) : null,
      placeholder: 'Selecione o módulo',
      label: 'Módulo',
      yup: Yup.mixed().required('Este campo é obrigatório!'),
    },
    {
      name: 'status',
      value: defaultValues.status ?? true,
      label: 'Status',
      yup: Yup.boolean(),
    },
  ];

  formInputs.forEach((object: any) => {
    yupschema[object.name] = object.yup;
    defaultValuesSchema[object.name] = object.value;
  });
  const schema = Yup.object().shape(yupschema);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValuesSchema,
  });

  const name = formInputs.find((field: any) => field.name === 'name');
  const module = formInputs.find((field: any) => field.name === 'school_class_id');
  const teachersField = formInputs.find((field: any) => field.name === 'teacher_ids');
  const status = formInputs.find((field: any) => field.name === 'status');
  const normalizedInstitutionId =
    defaultValues?.institution?.id ?? (institutionId ? Number(institutionId) : undefined);

  const onSubmit = async (values: any) => {
    const params = {
      ...values,
      institution_id: normalizedInstitutionId,
      school_class_id: values.school_class_id.value,
      teacher_ids: values.teacher_ids?.map((teacher: any) => teacher.value) ?? [],
    };
    const result = await reqApiTeams(
      defaultValues.name ? 'put' : 'post',
      params,
      defaultValues.name ? `${baseUrl}/${itemId}` : null
    );
    if (!isRequestFailure(result)) navigate(`/institutions/list/${institutionId}/teams`);
  };

  useEffect(() => {
    if (defaultValues.name) {
      reset(defaultValuesSchema);
    }
  }, [defaultValues]);

  const isEdit = Boolean(view ? 'Visualizar' : defaultValues.name);

  return (
    <FormShell
      title={isEdit ? 'Editar Turma' : 'Criar Turma'}
      backTo={`/institutions/list/${institutionId}/teams`}
      backLabel="Turmas"
    >
      <Box
        as="form"
        noValidate
        onSubmit={handleSubmit(onSubmit, validationToast)}
        display="flex"
        flexDirection="column"
        gap="16px"
      >
        <FormSection title="Informações Básicas">
          <FormRow>
            <InputForm
              name={name?.name}
              label={name?.label}
              placeholder={name?.placeholder}
              isRequired
              isDisabled={view}
              register={register}
              errors={errors}
            />
            <SwitchInputForm
              name={status?.name}
              label={status?.label}
              isDisabled={view}
              errors={errors}
              control={control}
            />
          </FormRow>

          <FormRow>
            <SelectInputForm
              name={module?.name}
              label={module?.label}
              options={[...(optionsModules?.map((item: any) => getFormattedLabel(item)) ?? [])]}
              isLoading={loadingModules}
              pagination={paginationModules}
              getOptions={(filters: any) => reqApiModules('get', { params: { ...filters } })}
              isRequired
              isDisabled={view}
              placeholder={module?.placeholder}
              control={control}
              errors={errors}
            />
            <SelectInputForm
              name={teachersField?.name}
              label={teachersField?.label}
              options={[...(optionsTeachers?.map((item: any) => getFormattedLabel(item)) ?? [])]}
              isLoading={loadingTeachers}
              pagination={paginationTeachers}
              getOptions={(filters: any) => reqApiTeachers('get', { params: { ...filters } })}
              isRequired
              isDisabled={view}
              placeholder={teachersField?.placeholder}
              isMulti
              control={control}
              errors={errors}
            />
          </FormRow>
        </FormSection>

        <FormActions
          cancelTo={`/institutions/list/${institutionId}/teams`}
          isLoading={isSubmitting}
        />
      </Box>
    </FormShell>
  );
}
